import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ClarifyPayload } from '@/lib/workflowStreamProtocol';

interface ClarifyingQuestionCardProps {
  clarify: ClarifyPayload;
  disabled?: boolean;
  onAnswer: (answer: string) => void;
}

export function ClarifyingQuestionCard({ clarify, disabled, onAnswer }: ClarifyingQuestionCardProps) {
  const [freeform, setFreeform] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleChip = (value: string) => {
    if (disabled || submitted) return;
    setSubmitted(true);
    onAnswer(value);
  };

  const handleFreeform = () => {
    if (disabled || submitted || !freeform.trim()) return;
    setSubmitted(true);
    onAnswer(freeform.trim());
  };

  return (
    <div className={cn('rounded-xl border border-border bg-card p-4 space-y-3', submitted && 'opacity-60')}>
      <div>
        <p className="text-sm font-medium">{clarify.question}</p>
        {clarify.reason && <p className="text-xs text-muted-foreground mt-1">{clarify.reason}</p>}
      </div>

      {clarify.options.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {clarify.options.map((opt, i) => (
            <button
              key={i}
              type="button"
              disabled={disabled || submitted}
              onClick={() => handleChip(opt.value ?? opt.label)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {clarify.allowFreeform && !submitted && (
        <div className="flex gap-2">
          <input
            type="text"
            value={freeform}
            onChange={(e) => setFreeform(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFreeform(); }}
            placeholder="or write your own…"
            disabled={disabled}
            className="flex-1 bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <Button size="sm" onClick={handleFreeform} disabled={disabled || !freeform.trim()}>
            Send
          </Button>
        </div>
      )}
    </div>
  );
}
